import React, { useState } from "react";
import { Plus, School, Edit2, Save, X, RefreshCw, Users, MapPin, Mail, Phone, BookOpen, ExternalLink, Copy } from "lucide-react";
import { updateExistingSchools } from "../../utils/updateSchools";
import UserManagementModal from "../modals/UserManagementModal";
import "../../styles/SchoolsView.css";

const SchoolsView = ({ schools, onCreateSchool, onUpdateSchool, currentUser, userRole, userSchoolRole }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [managingUsersForSchool, setManagingUsersForSchool] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    address: "",
    contactEmail: "",
    phone: "",
    yearbookAdvisorEmail: "",
    exportSettings: {
      csv: true,
      pspa: false,
      teacherease: false,
      skyward: false,
      fourup: false,
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchool) {
        await onUpdateSchool(editingSchool.id, formData);
        setEditingSchool(null);
        alert("School updated successfully!");
      } else {
        await onCreateSchool(formData);
        alert("School created successfully!");
      }
      setFormData({
        name: "",
        organization: "",
        address: "",
        contactEmail: "",
        phone: "",
        yearbookAdvisorEmail: "",
        exportSettings: {
          csv: true,
          pspa: false,
          teacherease: false,
          skyward: false,
          fourup: false,
        },
      });
      setShowForm(false);
    } catch (error) {
      alert("Error saving school: " + error.message);
    }
  };

  const handleEdit = (school) => {
    setEditingSchool(school);
    setFormData({
      name: school.name || "",
      organization: school.organization || "",
      address: school.address || "",
      contactEmail: school.contactEmail || "",
      phone: school.phone || "",
      yearbookAdvisorEmail: school.yearbookAdvisorEmail || "",
      exportSettings: school.exportSettings || {
        csv: true,
        pspa: false,
        teacherease: false,
        skyward: false,
        fourup: false,
      },
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSchool(null);
    setFormData({
      name: "",
      organization: "",
      address: "",
      contactEmail: "",
      phone: "",
      yearbookAdvisorEmail: "",
      exportSettings: {
        csv: true,
        pspa: false,
        teacherease: false,
        skyward: false,
        fourup: false,
      },
    });
  };

  const handleUpdateSubdomains = async () => {
    if (window.confirm("This will add subdomain URLs to all existing schools. Continue?")) {
      const success = await updateExistingSchools();
      if (success) {
        alert("Schools updated successfully! Refresh the page to see the changes.");
        window.location.reload();
      } else {
        alert("Error updating schools. Check console for details.");
      }
    }
  };

  const handleExportSettingChange = (exportType, checked) => {
    setFormData({
      ...formData,
      exportSettings: {
        ...formData.exportSettings,
        [exportType]: checked,
      },
    });
  };

  const handleCopyPortalUrl = async (subdomain) => {
    try {
      const url = `https://connect.iconikschools.com/${subdomain}`;
      await navigator.clipboard.writeText(url);
      alert("Portal URL copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy URL:", error);
      alert("Failed to copy URL. Please try again.");
    }
  };

  return (
    <div className="schools-view">
      <div className="card-header">
        <h2 className="card-title">Manage Schools</h2>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {schools.some(s => !s.subdomain) && (
            <button
              onClick={handleUpdateSubdomains}
              className="button button-secondary"
              title="Add subdomain URLs to existing schools"
              style={{ 
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db"
              }}
            >
              <RefreshCw style={{ width: "16px", height: "16px" }} />
              Update URLs
            </button>
          )}
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="button button-primary"
            >
              <Plus style={{ width: "16px", height: "16px" }} />
              Add School
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="schools-card">
          <h3 className="schools-form-title">
            {editingSchool ? "Edit School" : "Add New School"}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="schools-form-grid">
              <div className="input-group">
                <input
                  type="text"
                  placeholder="School Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="input"
                  required
                />
              </div>
              <div className="input-group">
                <input
                  type="text"
                  placeholder="Organization/District"
                  value={formData.organization}
                  onChange={(e) =>
                    setFormData({ ...formData, organization: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div className="input-group">
                <input
                  type="text"
                  placeholder="Address"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div className="input-group">
                <input
                  type="email"
                  placeholder="Contact Email"
                  value={formData.contactEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, contactEmail: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div className="input-group">
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div className="input-group">
                <input
                  type="email"
                  placeholder="Yearbook Advisor Email"
                  value={formData.yearbookAdvisorEmail}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      yearbookAdvisorEmail: e.target.value,
                    })
                  }
                  className="input"
                />
              </div>
            </div>

            <div className="export-settings-section">
              <h4 className="export-settings-title">Export Settings</h4>
              <p className="export-settings-description">
                Select which export formats this school needs:
              </p>
              <div className="export-settings-grid">
                <label className="export-setting-item">
                  <input
                    type="checkbox"
                    checked={formData.exportSettings.csv}
                    onChange={(e) =>
                      handleExportSettingChange("csv", e.target.checked)
                    }
                  />
                  <span>CSV Export</span>
                </label>
                <label className="export-setting-item">
                  <input
                    type="checkbox"
                    checked={formData.exportSettings.pspa}
                    onChange={(e) =>
                      handleExportSettingChange("pspa", e.target.checked)
                    }
                  />
                  <span>PSPA Export</span>
                </label>
                <label className="export-setting-item">
                  <input
                    type="checkbox"
                    checked={formData.exportSettings.teacherease}
                    onChange={(e) =>
                      handleExportSettingChange("teacherease", e.target.checked)
                    }
                  />
                  <span>TeacherEase Export</span>
                </label>
                <label className="export-setting-item">
                  <input
                    type="checkbox"
                    checked={formData.exportSettings.skyward}
                    onChange={(e) =>
                      handleExportSettingChange("skyward", e.target.checked)
                    }
                  />
                  <span>Skyward Export</span>
                </label>
                <label className="export-setting-item">
                  <input
                    type="checkbox"
                    checked={formData.exportSettings.fourup}
                    onChange={(e) =>
                      handleExportSettingChange("fourup", e.target.checked)
                    }
                  />
                  <span>4-Up Labels Export</span>
                </label>
              </div>
            </div>

            <div className="schools-form-actions">
              <button type="submit" className="button button-primary">
                {editingSchool ? (
                  <>
                    <Save style={{ width: "16px", height: "16px" }} />
                    Update School
                  </>
                ) : (
                  "Create School"
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="button button-outline"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="schools-card">
        <h3 className="schools-list-title">Schools ({schools.length})</h3>
        {schools.length === 0 ? (
          <div className="empty-state">
            <School className="empty-state-icon" />
            <h3 className="empty-state-title">No Schools Found</h3>
            <p className="empty-state-text">
              Create your first school to get started.
            </p>
          </div>
        ) : (
          <div className="schools-grid">
            {schools.map((school) => (
              <div key={school.id} className="school-item">
                <div className="school-main">
                  <div className="school-header">
                    <h3 className="school-title">{school.name}</h3>
                    {school.organization && <span className="school-org">{school.organization}</span>}
                  </div>
                  
                  <div className="school-actions">
                    {(userRole === "studio" || userSchoolRole === "school-admin") && (
                      <button
                        onClick={() => setManagingUsersForSchool(school)}
                        className="action-btn"
                        title="Manage Users"
                      >
                        <Users size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(school)}
                      className="action-btn"
                      title="Edit School"
                    >
                      <Edit2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="school-details">
                  {school.address && <div className="detail">{school.address}</div>}
                  {school.contactEmail && <div className="detail">{school.contactEmail}</div>}
                  {school.phone && <div className="detail">{school.phone}</div>}
                  {school.yearbookAdvisorEmail && <div className="detail">Yearbook: {school.yearbookAdvisorEmail}</div>}
                </div>

                {school.subdomain && (
                  <div className="school-link">
                    <a 
                      href={`https://connect.iconikschools.com/${school.subdomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="portal-link"
                    >
                      {school.subdomain}
                    </a>
                    <button
                      onClick={() => handleCopyPortalUrl(school.subdomain)}
                      className="copy-button"
                      title="Copy URL"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                )}

                {school.exportSettings && Object.values(school.exportSettings).some(Boolean) && (
                  <div className="school-tags">
                    {school.exportSettings.csv && <span className="tag">CSV</span>}
                    {school.exportSettings.pspa && <span className="tag">PSPA</span>}
                    {school.exportSettings.teacherease && <span className="tag">TE</span>}
                    {school.exportSettings.skyward && <span className="tag">Sky</span>}
                    {school.exportSettings.fourup && <span className="tag">4UP</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <UserManagementModal
        isOpen={!!managingUsersForSchool}
        onClose={() => setManagingUsersForSchool(null)}
        school={managingUsersForSchool}
        currentUser={currentUser}
        userRole={userRole}
        schoolRole={userSchoolRole}
      />
    </div>
  );
};

export default SchoolsView;
